// ==============================================================================
// FILE: src/App.tsx (COMPILATION BUG FIX)
// PURPOSE: This file contains the fix for the TypeScript error related to the
//          missing 'batchId' prop when rendering pages.
//
// CHANGE: The main component, `AppRoutes`, has been refactored. The logic for
//         rendering pages that require a `batchId` (like ClaimReviewPage) has
//         been separated from the main `switch` statement.
//
// REASONING: This change resolves the compilation error by ensuring that
//            components requiring a `batchId` are only ever rendered when a valid
//            ID is present in the context. The main `switch` statement now only
//            handles pages that do not require specific IDs, making the routing
//            logic clearer and preventing type errors.
// ==============================================================================
import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Snackbar, Alert, Button, IconButton, Tooltip } from '@mui/material';
import { Undo as UndoIcon } from '@mui/icons-material';
import { theme } from './styles/theme';
import { AppProvider, useAppContext } from './contexts/AppContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import IngestPage from './pages/IngestPage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import ClaimHistoryPage from './pages/ClaimHistoryPage';
import ClaimReviewPage from './pages/ClaimReviewPage';
import UserManagementPage from './pages/UserManagementPage';
import AppLayout from './components/AppLayout';
import AuditTrailPage from './pages/AuditTrailPage';
import ReportsPage from './pages/ReportsPage';
import ConsolidatedReportPage from './pages/ConsolidatedReportPage';
import ErrorSummaryPage from './pages/ErrorSummaryPage';
import BulkIngestPage from './pages/BulkIngestPage';
import BulkJobStatusDashboardPage from './pages/BulkJobStatusDashboardPage';
import ContractUploadPage from './pages/ContractUploadPage';

const AppRoutes = () => {
    const { user, activePage, reviewingBatchId, auditBatchId, errorBatchId, isSuperAdmin } = useAppContext();

    if (!user) {
        return <LoginPage />;
    }
    
    // --- THIS IS THE FIX ---
    // Handle all pages that require a specific ID in this block, before the main switch.
    // This guarantees that the `batchId` prop will always be a valid string.
    if (errorBatchId) {
        return (
            <AppLayout>
                <ErrorSummaryPage batchId={errorBatchId} />
            </AppLayout>
        );
    }

    if (auditBatchId) {
        return (
            <AppLayout>
                <AuditTrailPage batchId={auditBatchId} />
            </AppLayout>
        );
    }

    if (reviewingBatchId) {
        return (
            <AppLayout>
                <ClaimReviewPage batchId={reviewingBatchId} />
            </AppLayout>
        );
    }
    // --- END OF FIX ---


    // The main switch now only handles pages that do not require an ID.
    const renderPage = () => {
        switch (activePage) {
            case 'Dashboard':
                return <DashboardPage />;
            case 'Ingest':
                return <IngestPage />;
            case 'Bulk Ingest':
                return (user?.role === 'admin' || isSuperAdmin()) ? <BulkIngestPage /> : <DashboardPage />;
            case 'Bulk Job Status':
                return (user?.role === 'admin' || isSuperAdmin()) ? <BulkJobStatusDashboardPage /> : <DashboardPage />;
            case 'Review Queue':
                return <ReviewQueuePage />;
            case 'Claim History':
                return <ClaimHistoryPage />;
            case 'User Management':
                return <UserManagementPage />;
            case 'Reports':
                return <ReportsPage />;
            case 'Consolidated Reporting':
                return <ConsolidatedReportPage />;
            case 'Contract Master Data':
                return (user?.role === 'admin' || isSuperAdmin()) ? <ContractUploadPage /> : <DashboardPage />;
            default:
                return <DashboardPage />;
        }
    }

    return (
        <AppLayout>
            {renderPage()}
        </AppLayout>
    );
}

const NotificationHandler = () => {
    const { popUpNotification, hideNotification } = useAppContext();

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        if (popUpNotification.onCloseCommit) {
            popUpNotification.onCloseCommit();
        }
        hideNotification();
    };
    
    const action = popUpNotification.onUndo ? (
        <Tooltip title="Undo">
            <IconButton
                color="inherit"
                size="small"
                onClick={() => {
                    if(popUpNotification.onUndo) {
                        popUpNotification.onUndo();
                    }
                    hideNotification();
                }}
            >
                <UndoIcon fontSize="inherit" />
            </IconButton>
        </Tooltip>
    ) : null;

    return (
        <Snackbar
            open={popUpNotification.open}
            autoHideDuration={popUpNotification.onUndo ? 5000 : 6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
            <Alert 
                onClose={handleClose} 
                severity={popUpNotification.severity} 
                sx={{ width: '100%', alignItems: 'center' }} 
                action={action}
            >
                {popUpNotification.message}
            </Alert>
        </Snackbar>
    );
};

export default function App(): JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <AppProvider>
        <CssBaseline />
        <AppRoutes />
        <NotificationHandler />
      </AppProvider>
    </ThemeProvider>
  );
}

